import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import "./call-to-booking.css";

// Beat 2 of the home page. One ten second clip carries the whole promise, so
// the hand-built transcript and the four-job tab switcher that used to say the
// same thing twice are both gone and this section says it once.
//
// The four times are the clip's own, read frame by frame, which is why a row
// can light at the moment its picture arrives instead of on a timer that
// guesses. Nothing here claims a speed, and the agent in the clip never
// dispatches anyone, quotes a price or promises a time of arrival.
const BEATS = [
  {
    id: "rings",
    at: 0,
    name: "The phone rings",
    line: "A patient calls the practice while the front desk is busy.",
  },
  {
    id: "answers",
    at: 1.7,
    name: "Chleb answers",
    line: "It greets the caller in your words and asks what you would ask.",
  },
  {
    id: "books",
    at: 4.3,
    name: "It books the slot",
    line: "It offers a time your rules allow, then puts it on your calendar.",
  },
  {
    id: "text",
    at: 7.3,
    name: "The customer gets a text",
    line: "A confirmation goes out with the day and the time.",
  },
] as const;

const CLIP = "/call-to-booking.mp4";
const CLIP_MOBILE = "/call-to-booking-m.mp4";
const POSTER = "/call-to-booking-poster.webp";
// A row lights fractionally before its frame lands rather than fractionally
// after, so the eye is already on the row when the picture changes.
const TOLERANCE = 0.12;
// Enough thresholds that the half-in-view test below is evaluated all the way
// through the section's entrance, including on a phone where the section is
// taller than the window and its ratio can never reach 0.5 at all.
const STEPS = Array.from({ length: 11 }, (_, i) => i / 10);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function beatAt(time: number) {
  let index = 0;
  for (let i = 0; i < BEATS.length; i += 1)
    if (time >= BEATS[i].at - TOLERANCE) index = i;
  return index;
}

export function CallToBooking() {
  const section = useRef<HTMLElement>(null);
  const clip = useRef<HTMLVideoElement>(null);
  const startedOnce = useRef(false);
  // `near` is the whole lazy-load contract: until it flips there is neither a
  // poster nor a <video> in the document, so not one byte of this section is
  // part of the hero's first load. The clip is gated on reduced motion as
  // well; the poster is not, because reduced motion still wants the picture.
  const [near, setNear] = useState(false);
  const [painted, setPainted] = useState(false);
  const [active, setActive] = useState(0);
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    const preference = () => setReduced(media.matches);
    preference();
    media.addEventListener("change", preference);
    return () => media.removeEventListener("change", preference);
  }, []);

  // (1) Near the section: allow the fetch. Half a viewport of warning is
  // plenty for the files to arrive before anything is asked of them, and the
  // hero is more than two viewports tall, so this can never fire on load.
  useEffect(() => {
    const element = section.current;
    if (!element || near) return;
    const approach = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          approach.disconnect();
        }
      },
      { rootMargin: "60% 0px" },
    );
    approach.observe(element);
    return () => approach.disconnect();
  }, [near]);

  // (2) Half the section in view: play, once, ever. The clip holds on its last
  // frame with the confirmation still on screen; it never loops, and it can
  // never run above the fold because half of this section cannot be on screen
  // while the hero is.
  useEffect(() => {
    const element = section.current;
    const video = clip.current;
    if (!element || !video || reduced) return;
    const halfShown = (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting) return false;
      if (entry.intersectionRatio >= 0.5) return true;
      const window_ = entry.rootBounds?.height ?? innerHeight;
      const reach = Math.min(entry.boundingClientRect.height, window_);
      return entry.intersectionRect.height >= reach * 0.5;
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (startedOnce.current || !halfShown(entry)) return;
        startedOnce.current = true;
        // A browser that refuses the play leaves the poster and the Replay
        // pill, which is a complete section either way.
        void Promise.resolve(video.play()).catch(() => {});
        observer.disconnect();
      },
      { threshold: STEPS },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near, reduced]);

  const follow = useCallback(() => {
    const video = clip.current;
    if (video) setActive(beatAt(video.currentTime));
  }, []);

  // Seeking keeps the play state: a row clicked mid-play carries on from
  // there, a row clicked once the clip has finished holds that frame.
  const jump = (index: number) => {
    setActive(index);
    const video = clip.current;
    if (video) video.currentTime = BEATS[index].at;
  };

  const replay = () => {
    const video = clip.current;
    if (!video) return;
    startedOnce.current = true;
    setActive(0);
    video.currentTime = 0;
    void Promise.resolve(video.play()).catch(() => {});
  };

  const body = (beat: (typeof BEATS)[number]) => (
    <>
      <span className="ctb-dot" aria-hidden="true" />
      <span className="ctb-beat-body">
        <strong>{beat.name}</strong>
        <span>{beat.line}</span>
      </span>
    </>
  );

  return (
    <section className="ctb g-tone" id="how-it-works" ref={section}>
      <div className="rb-wrap ctb-grid">
        <div className="ctb-lead" data-sc-in data-sc-stagger="60">
          <h2 className="g-h2">Here’s what happens when your phone rings.</h2>
          <p className="g-lede">
            One call from beginning to end: the phone rings, Chleb answers, the
            appointment goes on your calendar, and the customer gets a text
            confirming it.
          </p>
        </div>

        <div className="ctb-media-col" data-sc-in>
          <figure className="ctb-frame g-panel g-panel--strong">
            <div className="ctb-media">
              {near && (
                <img
                  src={POSTER}
                  alt="A phone on a desk showing an incoming call."
                  width={1280}
                  height={720}
                  loading="lazy"
                  decoding="async"
                />
              )}
              {near && !reduced && (
                <video
                  ref={clip}
                  className={painted ? "is-painted" : undefined}
                  // The 720 wide file on phones, the 1280 on everything else.
                  // Picked here rather than with a media attribute on a
                  // <source>, which browsers honour inconsistently.
                  src={
                    typeof window !== "undefined" &&
                    window.matchMedia("(max-width: 767px)").matches
                      ? CLIP_MOBILE
                      : CLIP
                  }
                  muted
                  playsInline
                  // "preload none until the section is near" is implemented as
                  // no element and no request at all until then; once the
                  // element exists the file is wanted, so it is fetched.
                  preload="auto"
                  onLoadedData={() => setPainted(true)}
                  onTimeUpdate={follow}
                  onSeeked={follow}
                  onEnded={() => setActive(BEATS.length - 1)}
                />
              )}
            </div>
            <figcaption className="ctb-note">
              Illustrative sequence. Not a recording of a customer call.
            </figcaption>
          </figure>
          {!reduced && (
            <div className="ctb-controls">
              <button type="button" className="g-pill" onClick={replay}>
                <RotateCcw size={15} aria-hidden="true" /> Replay
              </button>
            </div>
          )}
        </div>

        {reduced ? (
          <ul className="ctb-beats ctb-beats--static">
            {BEATS.map((beat) => (
              <li className="ctb-beat" key={beat.id}>
                {body(beat)}
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="ctb-beats"
            role="group"
            aria-label="The four moments in the sequence"
            /* The reveal goes on the group and never on the rows. The engine
               writes `sc-in` straight onto the DOM node, and React rewrites a
               row's className every time the active beat changes, which would
               wipe it and leave that row at opacity 0 for the whole visit. */
            data-sc-in
          >
            {BEATS.map((beat, i) => (
              <button
                type="button"
                key={beat.id}
                className={
                  "ctb-beat" +
                  (i === active ? " is-on" : i < active ? " is-past" : "")
                }
                aria-pressed={i === active}
                onClick={() => jump(i)}
              >
                {body(beat)}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
